// services/analytics.ts

import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
} from "firebase/analytics";
import { track } from "@vercel/analytics/react";
import * as amplitude from "@amplitude/analytics-browser";

import { fbApp } from "@/services/firebase";

let analytics: Analytics | null = null;
let analyticsInitialized = false;
let analyticsInitPromise: Promise<void> | null = null;
let amplitudeInitialized = false;

// Initialize Firebase Analytics
const initializeAnalytics = async (): Promise<void> => {
  if (analyticsInitPromise) {
    return analyticsInitPromise;
  }

  analyticsInitPromise = isSupported()
    .then(supported => {
      if (supported) {
        try {
          analytics = getAnalytics(fbApp);
          analyticsInitialized = true;
        } catch (error) {
          // Silently handle Firebase configuration errors
          // eslint-disable-next-line no-console
          console.warn(
            "Firebase Analytics initialization failed - this is expected if domain is not configured in Firebase Console:",
            error
          );
          analyticsInitialized = false;
        }
      }
    })
    .catch(error => {
      // Silently handle Firebase configuration errors
      // eslint-disable-next-line no-console
      console.warn(
        "Firebase Analytics not supported or failed to initialize - this is expected if domain is not configured in Firebase Console:",
        error
      );
      analyticsInitialized = false;
    });

  return analyticsInitPromise;
};

// Initialize Amplitude analytics
const initializeAmplitude = async (): Promise<void> => {
  if (amplitudeInitialized) {
    return;
  }

  try {
    const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

    if (!apiKey) {
      // eslint-disable-next-line no-console
      console.warn("Amplitude API key not found in environment variables");

      return;
    }

    // Keep the lightweight auto-tracking (page views, sessions, attribution)
    // but disable the DOM-observing captures — element/form interaction and
    // download tracking pull in the heavier @amplitude/plugin-autocapture
    // work and add main-thread cost. App events are logged explicitly via
    // logEvent, so those DOM observers are largely redundant here.
    amplitude.init(apiKey, {
      autocapture: {
        elementInteractions: false,
        formInteractions: false,
        fileDownloads: false,
      },
    });
    amplitudeInitialized = true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to initialize Amplitude Analytics:", error);
  }
};

// Initialize analytics at browser idle so Firebase + Amplitude setup runs
// OFF the critical path (it previously ran eagerly on module load, which is
// during hydration for any page that imports logEvent). Events fired before
// init are handled by the existing `*Initialized` guards; Amplitude also
// buffers pre-init track calls.
if (typeof window !== "undefined") {
  const bootAnalytics = () => {
    initializeAnalytics();
    initializeAmplitude();
  };

  const ric = (window as any).requestIdleCallback;

  if (typeof ric === "function") {
    ric(bootAnalytics, { timeout: 4000 });
  } else {
    setTimeout(bootAnalytics, 2500);
  }
}

// Set user ID for Amplitude analytics
export const setAnalyticsUserId = async (userId: string): Promise<void> => {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  try {
    if (!amplitudeInitialized) {
      await initializeAmplitude();
    }
    if (amplitudeInitialized) {
      try {
        amplitude.setUserId(userId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to set Amplitude user ID:", error);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to set analytics user ID:", error);
  }
};

// Set user properties for Amplitude analytics
export const setAnalyticsUserProperties = async (
  userProperties: Record<string, string | number | boolean>
): Promise<void> => {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  try {
    if (!amplitudeInitialized) {
      await initializeAmplitude();
    }
    if (amplitudeInitialized) {
      try {
        const identify = new amplitude.Identify();

        Object.entries(userProperties).forEach(([key, value]) => {
          identify.set(key, value);
        });
        amplitude.identify(identify);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to set Amplitude user properties:", error);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to set analytics user properties:", error);
  }
};

// Clear user identification (for logout)
export const clearAnalyticsUser = async (): Promise<void> => {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  try {
    if (!amplitudeInitialized) {
      await initializeAmplitude();
    }
    if (amplitudeInitialized) {
      try {
        amplitude.setUserId(undefined);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to clear Amplitude user ID:", error);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear analytics user:", error);
  }
};

// Main analytics logging function
export const logEvent = async (
  eventName: string,
  eventParams?: Record<string, string | number | boolean | undefined>
): Promise<void> => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log(`Analytics Event: ${eventName}`, eventParams);

    return;
  }

  const prefixedEventName = `fwww_${eventName}`;

  // Filter out undefined values to ensure compatibility with analytics services
  const filteredParams = eventParams
    ? (Object.fromEntries(
        Object.entries(eventParams).filter(([_, value]) => value !== undefined)
      ) as Record<string, string | number | boolean>)
    : undefined;

  try {
    // Vercel analytics
    try {
      track(prefixedEventName, filteredParams);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to log Vercel Analytics event:", error);
    }

    // Firebase Analytics
    if (!analyticsInitialized) {
      await initializeAnalytics();
    }
    if (analytics && analyticsInitialized) {
      try {
        firebaseLogEvent(analytics, prefixedEventName, filteredParams);
      } catch (error) {
        // Silently handle Firebase errors - domain may not be configured
        // eslint-disable-next-line no-console
        console.warn(
          "Firebase Analytics event logging failed - domain may not be configured in Firebase Console:",
          error
        );
      }
    }

    // Amplitude analytics
    if (!amplitudeInitialized) {
      await initializeAmplitude();
    }
    if (amplitudeInitialized) {
      try {
        amplitude.track(prefixedEventName, filteredParams);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to log Amplitude Analytics event:", error);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Analytics logging failed:", error);
  }
};

// Convenience functions for common events
export const logPageView = async (
  pageName: string,
  additionalParams?: Record<string, string | number | boolean>
) => {
  return logEvent("page_view", {
    page_name: pageName,
    ...additionalParams,
  });
};

export const logUserAction = async (
  action: string,
  additionalParams?: Record<string, string | number | boolean>
) => {
  return logEvent("user_action", {
    action,
    ...additionalParams,
  });
};

export const logAuthEvent = async (
  authEvent: string,
  additionalParams?: Record<string, string | number | boolean>
) => {
  return logEvent("auth_event", {
    auth_event: authEvent,
    ...additionalParams,
  });
};

export const logError = async (
  errorType: string,
  errorMessage: string,
  additionalParams?: Record<string, string | number | boolean>
) => {
  return logEvent("error", {
    error_type: errorType,
    error_message: errorMessage,
    ...additionalParams,
  });
};
